import { useInfiniteQuery } from "@tanstack/react-query";

import { API_BASE_URL } from "@/config";
import { isRecord, validatedJson } from "@/lib/ui/validatedJson";

export type PublicBuylistCard = {
  public_id: string;
  name: string;
  image_url: string | null;
  game: string | null;
  set_name: string | null;
  collector_number: string | null;
  hot: boolean;
  price_at_table: boolean;
  cash: string | number | null;
  trade: string | number | null;
};

export type PublicBuylistPage = {
  shop_name: string;
  shop_slug: string;
  public_note: string | null;
  as_of: string | null;
  cards: PublicBuylistCard[];
  rules: PublicBuylistRule[];
  next_cursor: string | null;
};

export type PublicBuylistRule = {
  game: string;
  sets: string[];
  rarities: string[];
  min_market: string | number;
  cash_pct: string | number;
  trade_pct: string | number;
};

export class BuylistUnavailable extends Error {
  constructor(readonly shopName: string) {
    super("Buylist not published");
  }
}

function isCard(value: unknown): value is PublicBuylistCard {
  return isRecord(value) && typeof value.public_id === "string"
    && typeof value.name === "string" && typeof value.hot === "boolean"
    && typeof value.price_at_table === "boolean"
    && ["image_url", "game", "set_name", "collector_number"].every((key) =>
      value[key] === null || typeof value[key] === "string"
    )
    && ["cash", "trade"].every((key) =>
      value[key] === null || typeof value[key] === "string" || typeof value[key] === "number"
    );
}

function isRule(value: unknown): value is PublicBuylistRule {
  return isRecord(value) && typeof value.game === "string"
    && Array.isArray(value.sets) && value.sets.every((item) => typeof item === "string")
    && Array.isArray(value.rarities) && value.rarities.every((item) => typeof item === "string")
    && ["min_market", "cash_pct", "trade_pct"].every((key) =>
      typeof value[key] === "string" || typeof value[key] === "number"
    );
}

export function isPublicBuylistPage(value: unknown): value is PublicBuylistPage {
  return isRecord(value) && typeof value.shop_name === "string"
    && typeof value.shop_slug === "string"
    && (value.public_note === null || typeof value.public_note === "string")
    && (value.as_of === null || typeof value.as_of === "string")
    && Array.isArray(value.cards) && value.cards.every(isCard)
    && Array.isArray(value.rules) && value.rules.every(isRule)
    && (value.next_cursor === null || typeof value.next_cursor === "string");
}

export function usePublicBuylist(
  slug: string | null,
  query: string,
  game: string | null,
  setName: string | null,
  sort: "cash" | "name" | "set",
) {
  return useInfiniteQuery({
    queryKey: ["public-buylist", slug, query.trim(), game, setName, sort],
    enabled: !!slug,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<PublicBuylistPage> => {
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      if (game) params.set("game", game);
      if (setName) params.set("set_name", setName);
      if (sort !== "cash") params.set("sort", sort);
      if (pageParam) params.set("cursor", pageParam);
      const response = await fetch(
        `${API_BASE_URL}/shops/${encodeURIComponent(slug!)}/buylist?${params}`,
      );
      if (response.status === 404) {
        const body = await response.json();
        throw new BuylistUnavailable(body?.detail?.shop_name ?? slug!);
      }
      if (!response.ok) throw new Error("Couldn't load buylist");
      return validatedJson(response, isPublicBuylistPage, "Buylist response was invalid");
    },
    getNextPageParam: (last) => last.next_cursor,
  });
}
